import React, { useEffect, useState } from "react";
import Seo from "../../../../Components/SeoComponent/Seo";
import Header from "../../../../Components/Header";
import { useService } from "../../../../context/ServiceContext";
import {
  AlertCircle,
  ArrowRight,
  User,
  Phone,
  CreditCard,
  Copy,
  FileJson,
  Info,
  ListCheck,
  Loader,
  Loader2,
} from "lucide-react";
import { fetchProfile } from "../../../../Services/ApiTestService/aadharTestService";
import { getProfileFetchV2Details } from "../../../../Services/ApiTestService/apiTestService";
import { showErrorToast, showSuccessToast } from "../../../../utils/toastUtils";
import ApiResponseViewer from "./ResData";

function ProfileFetchV2() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [mobileNumber, setMobileNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();

  const [profileData, setProfileData] = useState(null);

  // Basic Validation Checkers
  const isValidMobile = (val) => /^[0-9]{10}$/.test(val);
  const isValidPan = (val) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(val);

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
    setTimeout(() => setIsJsonCopied(false), 2000);
  };

  const handleProfileFetch = async () => {
    if (!profileData) return;

    // Validation: At least one field is required
    if (!mobileNumber && !panNumber) {
      return setInputError("At least 1 field (Mobile or PAN) is required.");
    }

    if (mobileNumber && !isValidMobile(mobileNumber)) {
      return setInputError("⚠️ Please enter a valid 10-digit Mobile Number.");
    }

    if (panNumber && !isValidPan(panNumber)) {
      return setInputError("⚠️ Please enter a valid 10-character PAN Number.");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getProfileFetchV2Details({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        mobile: mobileNumber.trim(),
        pan: panNumber.trim()?.toUpperCase(),
      });

      setResponse(res);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else {
        showErrorToast(
          res?.data?.message ||
            res?.mesage ||
            res?.data?.data?.message ||
            res?.data?.error?.message ||
            res?.status?.message ||
            "something went wrong",
        );
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName || "Profile Fetch V2"} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin text-blue-600" />
        </div>
      )}

      {/* Mesh Gradient Background Wrapper */}
      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-50 via-slate-50 to-indigo-50/30">
        {/* ---------------- FORM (Glassmorphism UI) ---------------- */}
        <div className="max-w-4xl w-full mt-2 rounded-3xl border border-white/60 bg-white/40 backdrop-blur-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {/* Header */}
          <div className="flex items-center gap-4 border-b border-slate-200/60 pb-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30">
              <User size={28} />
            </div>

            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Profile Fetch V2
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Retrieve user profile details using either Mobile or PAN number.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-8 flex flex-col md:flex-row items-center gap-4 md:gap-6 relative z-10 w-full">
            {/* Mobile Number */}
            <div className="group w-full flex-1">
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Phone size={17} className="text-indigo-500" />
                Mobile Number
              </label>
              <input
                type="tel"
                maxLength={10}
                value={mobileNumber}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setMobileNumber(val);
                  setPanNumber("");
                }}
                placeholder="Eg: 9876543210"
                className="w-full rounded-xl border border-black/20 bg-white/50 px-4 py-3.5 text-sm transition-all duration-300 placeholder:text-slate-400 focus:bg-white focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 outline-none shadow-sm"
              />
            </div>

            {/* OR Badge */}
            <div className="flex items-center justify-center shrink-0 mt-0 md:mt-7">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/60 backdrop-blur-xl border border-black/20 shadow-[0_4px_15px_rgb(0,0,0,0.05)] text-slate-500 font-bold text-xs tracking-widest z-20">
                OR
              </div>
            </div>

            {/* PAN Number */}
            <div className="group w-full flex-1">
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <CreditCard size={17} className="text-blue-500" />
                PAN Number
              </label>
              <input
                type="text"
                maxLength={10}
                value={panNumber}
                onChange={(e) => {
                  setPanNumber(e.target.value.toUpperCase());
                  setMobileNumber("");
                }}
                placeholder="Eg: ABCDE1234F"
                className="w-full rounded-xl border uppercase border-black/20 bg-white/50 px-4 py-3.5 text-sm transition-all duration-300 placeholder:text-slate-400 focus:bg-white focus:border-blue-400 focus:ring-4 focus:ring-blue-100 outline-none shadow-sm"
              />
            </div>
          </div>

          {/* Error */}
          {inputError && (
            <div className="mt-6 flex items-center gap-3 rounded-xl border border-red-200/80 bg-red-50/80 backdrop-blur-sm px-5 py-4 text-red-700 shadow-sm animate-in fade-in slide-in-from-top-2">
              <AlertCircle size={18} />
              <span className="text-sm font-semibold">{inputError}</span>
            </div>
          )}

          {/* Button */}
          <div className="mt-8 flex justify-end">
            <button
              disabled={isLoading || (!mobileNumber && !panNumber)}
              onClick={handleProfileFetch}
              className="group relative inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-3.5 font-semibold text-white shadow-[0_8px_20px_-6px_rgba(79,70,229,0.5)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_25px_-6px_rgba(79,70,229,0.6)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Fetching...
                </>
              ) : (
                <>
                  Fetch Profile
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Response Message */}
        {response && (
          <div
            className={`mt-6 ${
              response.status?.code !== 200 ? "text-red-500" : "text-green-600"
            } py-2 text-xl font-bold animate-in fade-in`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* Instructions */}
        <div className="w-full flex justify-center px-4">
          <div className="bg-blue-50/80 backdrop-blur-md max-w-3xl border border-blue-200/60 text-blue-800 p-5 mt-8 rounded-2xl shadow-sm">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info size={20} /> Important Instructions{" "}
              <span className="text-red-500">*</span>
            </h3>
            <p className="text-sm leading-relaxed opacity-90">
              Please enter either a valid 10-digit Mobile Number or a
              10-character PAN Number. After clicking the fetch button, the
              system will return the structured profile data associated with the
              provided details.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl px-4 animate-in slide-in-from-bottom-4 duration-500">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-slate-900/95 relative text-emerald-300 p-6 rounded-2xl overflow-auto shadow-2xl border border-slate-700">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-slate-300 hover:text-white top-5 right-6 transition-colors bg-white/10 p-2 rounded-lg backdrop-blur-sm"
              >
                {isJsonCopied ? (
                  <span className="text-xs font-bold text-emerald-400">
                    Copied!
                  </span>
                ) : (
                  <Copy size={18} />
                )}
              </button>
              <p className="font-bold mb-4 text-xl flex items-center justify-start gap-2 text-white">
                <FileJson className="text-indigo-400" /> JSON Response
              </p>
              <hr className="mb-6 border-slate-700" />
              <pre className="text-[14px] font-mono leading-relaxed">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- PARSED DATA VIEW -------- */}
           <ApiResponseViewer response={response}/>
          </div>
        )}
      </div>
    </>
  );
}

export default ProfileFetchV2;
